import React, { useState } from "react";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

export default function MedmateLinkingPanel() {
  const [searchTerm, setSearchTerm] = useState("");
  const [chemists, setChemists] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const [selectedChemist, setSelectedChemist] = useState(null);
  const [medmateId, setMedmateId] = useState("");

  const [linkLoading, setLinkLoading] = useState(false);
  const [linkResult, setLinkResult] = useState(null); // { type: "ok"|"err"|"conflict", text, conflictName }

  const runSearch = async () => {
    const term = searchTerm.trim();
    if (!term) return;
    setSearchLoading(true);
    setSearchError(null);
    setSelectedChemist(null);
    setMedmateId("");
    setLinkResult(null);
    setHasSearched(true);
    try {
      const data = await phappsRequest(API_PATHS.MEDMATE_SEARCH_CHEMISTS, "GET", null, { q: term });
      setChemists(Array.isArray(data.chemists) ? data.chemists : []);
    } catch (e) {
      setSearchError(e.message || "Search failed.");
      setChemists([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const selectChemist = (c) => {
    const alreadySelected = selectedChemist?.ChemistID === c.ChemistID;
    if (alreadySelected) {
      setSelectedChemist(null);
      setMedmateId("");
    } else {
      setSelectedChemist(c);
      setMedmateId(c.MedmateProviderID != null ? String(c.MedmateProviderID) : "");
    }
    setLinkResult(null);
  };

  const runLink = async (force = false) => {
    if (!selectedChemist || !medmateId.trim() || linkLoading) return;
    setLinkLoading(true);
    setLinkResult(null);
    try {
      const res = await phappsRequest(API_PATHS.MEDMATE_LINK_CLINIC, "POST", {
        ChemistID: selectedChemist.ChemistID,
        MedmateProviderID: medmateId.trim(),
        force,
      });

      if (res.success) {
        // Update local list so the badge reflects the new ID immediately
        const newId = medmateId.trim();
        setChemists((prev) =>
          prev.map((c) =>
            c.ChemistID === selectedChemist.ChemistID
              ? { ...c, MedmateProviderID: newId }
              : c
          )
        );
        setSelectedChemist((prev) => prev ? { ...prev, MedmateProviderID: newId } : prev);
        setLinkResult({
          type: "ok",
          text: res.alreadyLinked
            ? `${selectedChemist.ChemistName} is already linked to Medmate ID ${newId}. No changes made.`
            : `Successfully linked ${selectedChemist.ChemistName} → Medmate ID ${newId}.`,
        });
      } else if (res.conflict) {
        setLinkResult({
          type: "conflict",
          text: `Medmate ID ${medmateId.trim()} is already connected to ${res.conflictChemistName}.`,
          conflictName: res.conflictChemistName,
        });
      } else {
        setLinkResult({ type: "err", text: "Link could not be completed." });
      }
    } catch (e) {
      setLinkResult({ type: "err", text: e.message || "Link failed." });
    } finally {
      setLinkLoading(false);
    }
  };

  const resetLinkResult = () => setLinkResult(null);

  const isAlreadyLinked = !!selectedChemist?.MedmateProviderID;
  const idChanged = medmateId.trim() !== String(selectedChemist?.MedmateProviderID ?? "");
  const canMatch = !!selectedChemist && medmateId.trim().length > 0 && !linkLoading;

  return (
    // Two-column layout on medium+ screens; single column on small
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">

      {/* ── LEFT: Search + Results ── */}
      <div className="space-y-4">
        {/* Search box */}
        <section className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3">
          <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
            Search pharmacy
          </h3>
          <div className="flex gap-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="Pharmacy name…"
              className="flex-1 min-w-0 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
              disabled={searchLoading}
            />
            <button
              type="button"
              onClick={runSearch}
              disabled={searchLoading || !searchTerm.trim()}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
            >
              {searchLoading ? "…" : "Search"}
            </button>
          </div>
          {searchError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {searchError}
            </p>
          )}
        </section>

        {/* Results list */}
        {hasSearched && (
          <section className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100">
              <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                {chemists.length === 0 ? "No results" : `${chemists.length} pharmacy${chemists.length !== 1 ? "s" : ""} found`}
              </h3>
            </div>
            {chemists.length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center">Try a different search term.</p>
            ) : (
              <ul className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
                {chemists.map((c) => {
                  const active = selectedChemist?.ChemistID === c.ChemistID;
                  return (
                    <li key={c.ChemistID}>
                      <button
                        type="button"
                        onClick={() => selectChemist(c)}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                          active
                            ? "bg-blue-50 border-l-4 border-l-blue-500"
                            : "hover:bg-slate-50 border-l-4 border-l-transparent"
                        }`}
                      >
                        <span className="block font-medium text-slate-800">{c.ChemistName}</span>
                        {c.MedmateProviderID ? (
                          <span className="inline-flex items-center gap-1 mt-0.5 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                            Medmate ID: {c.MedmateProviderID}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 mt-0.5 text-xs text-slate-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-300 inline-block" />
                            Not linked
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}
      </div>

      {/* ── RIGHT: Match panel ── */}
      <div>
        {!selectedChemist ? (
          <div className="rounded-xl border-2 border-dashed border-slate-200 p-8 text-center text-slate-400 text-sm">
            Select a pharmacy on the left to link or update its Medmate ID.
          </div>
        ) : (
          <section className="rounded-xl border border-slate-200 overflow-hidden">
            {/* Header */}
            <div className={`px-4 py-3 border-b ${isAlreadyLinked ? "bg-emerald-50 border-emerald-100" : "bg-blue-50 border-blue-100"}`}>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-0.5">
                {isAlreadyLinked ? "Update linking" : "New linking"}
              </p>
              <p className="font-semibold text-slate-800 text-sm">{selectedChemist.ChemistName}</p>
              {isAlreadyLinked && (
                <p className="text-xs text-emerald-700 mt-0.5">
                  Currently linked → Medmate ID: <strong>{selectedChemist.MedmateProviderID}</strong>
                </p>
              )}
            </div>

            <div className="p-4 space-y-4">
              {/* ID input */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                  Medmate Provider ID
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={medmateId}
                    onChange={(e) => {
                      setMedmateId(e.target.value);
                      resetLinkResult();
                    }}
                    placeholder="Enter Medmate Provider ID…"
                    disabled={linkLoading}
                    className="flex-1 min-w-0 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                  />
                  {medmateId && (
                    <button
                      type="button"
                      onClick={() => { setMedmateId(""); resetLinkResult(); }}
                      disabled={linkLoading}
                      className="px-3 py-2 rounded-lg border border-slate-200 text-slate-500 text-sm hover:bg-slate-50"
                      title="Clear"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* Match button */}
              <button
                type="button"
                onClick={() => runLink(false)}
                disabled={!canMatch}
                className="w-full py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {linkLoading
                  ? "Matching…"
                  : isAlreadyLinked && !idChanged
                  ? "Confirm existing link"
                  : isAlreadyLinked
                  ? "Update Medmate ID"
                  : "Match"}
              </button>

              {/* Conflict warning */}
              {linkResult?.type === "conflict" && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 space-y-3">
                  <p className="text-sm text-amber-800 font-medium">
                    Medmate ID <strong>{medmateId.trim()}</strong> is already connected to{" "}
                    <strong>{linkResult.conflictName}</strong>.
                  </p>
                  <p className="text-xs text-amber-700">
                    Do you want to reassign it to <strong>{selectedChemist.ChemistName}</strong>?
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => runLink(true)}
                      disabled={linkLoading}
                      className="px-4 py-1.5 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
                    >
                      {linkLoading ? "Linking…" : "Yes, override"}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setMedmateId(""); resetLinkResult(); }}
                      className="px-4 py-1.5 rounded-lg border border-slate-300 bg-white text-sm text-slate-700 hover:bg-slate-50"
                    >
                      Change ID
                    </button>
                  </div>
                </div>
              )}

              {/* Success / error */}
              {linkResult?.type === "ok" && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3 space-y-2">
                  <p className="text-sm text-emerald-800">{linkResult.text}</p>
                  <button
                    type="button"
                    onClick={() => { resetLinkResult(); setSelectedChemist(null); setMedmateId(""); }}
                    className="text-xs font-medium text-emerald-700 hover:underline"
                  >
                    Link another pharmacy →
                  </button>
                </div>
              )}

              {linkResult?.type === "err" && (
                <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-2 text-sm text-red-700">
                  {linkResult.text}
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
